/**
 * RecordingConfigPanel — per-camera recording mode + trigger configuration.
 *
 * Shared by the local and cloud dashboards. The parent owns the transport (local calls the
 * backend directly, cloud goes through the tunnel), so this component takes plain
 * `config` / `onSave` props and knows nothing about either.
 *
 * WHY NOT ON THE ONVIF CONFIG PAGE: that page loads ONVIF config + capabilities and error-
 * banners the whole view when they fail, so it degrades on non-ONVIF or unreachable
 * cameras. Recording configuration has to work on every camera.
 */
import { useState, useEffect, useMemo } from 'react';
import StatusTag from '../common/StatusTag';
import {
  MODE_LABELS, EVENT_TYPE_LABELS, TIMER_DRIVEN_EVENT_TYPES, MOTION_SOURCE_LABELS, FIELD_HINTS,
  motionThresholdFor, motionSensitivityMeaning,
} from './recordingVocab';

const inputCls =
  'w-full rounded-lg border border-border bg-muted px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:opacity-40';
const labelCls = 'text-[11px] font-medium text-muted-foreground mb-1 block';
const sectionCls = 'text-[13px] font-semibold text-foreground';

export interface RecordingConfig {
  camera_id?: string;
  enabled: boolean;
  mode: 'continuous' | 'event' | 'motion' | 'event_motion';
  event_types: string[];
  min_confidence: number;
  motion_source: 'auto' | 'onvif' | 'bitrate' | 'pixel';
  motion_sensitivity: number;
  motion_min_duration_ms: number;
  motion_idle_timeout_ms: number;
  pre_roll_seconds: number;
  post_roll_seconds: number;
  max_clip_seconds: number;
  retention_days: number | null;
}

export interface RecordingSystemDefaults {
  segment_seconds?: number;
  fragment_ttl_seconds?: number;
  retention_days?: number;
  retention_days_triggered?: number;
  max_pre_roll_seconds?: number;
  retention_enforce?: 'log' | 'enforce';
}

export interface RecordingConfigPanelProps {
  config: RecordingConfig | null;
  defaults?: RecordingSystemDefaults;
  /** Server-supplied vocabulary; falls back to the built-in lists when absent. */
  options?: { modes?: string[]; motion_sources?: string[]; event_types?: string[] };
  /** Average stored bitrate in kbps, for the storage estimate. From recording_segments. */
  bitrateKbps?: number | null;
  canEdit?: boolean;
  loading?: boolean;
  /**
   * Live motion state. `state`/`score`/`threshold` come from the recorder's stream detector;
   * `onvif` from the camera-side subscription (null when there isn't one).
   */
  motion?: {
    state?: string;
    score?: number | null;
    threshold?: number | null;
    onvif?: {
      state?: string;
      motion_active?: boolean;
      last_motion_at?: string | null;
      confirmed_working?: boolean;
      clock_skew_seconds?: number | null;
      error?: string | null;
    } | null;
  } | null;
  /** Whether the camera advertises ONVIF motion topics (from the capability probe). */
  motionCapability?: { supported?: boolean; topics?: string[] } | null;
  onSave: (patch: Partial<RecordingConfig>) => void | Promise<void>;
  /** Phase gating — motion lands after the retention engine, events after the CC tap. */
  eventModeAvailable?: boolean;
  motionModeAvailable?: boolean;
}

/** Per-field explanation, rendered under the input it describes. */
const hintCls = 'mt-1 text-[10.5px] leading-snug text-muted-foreground';

const GB_PER_DAY = (kbps: number, dutyFraction: number) =>
  ((kbps * 1000) / 8) * 86400 * dutyFraction / 1024 ** 3;

export default function RecordingConfigPanel({
  config,
  defaults = {},
  options = {},
  bitrateKbps = null,
  canEdit = false,
  loading = false,
  motion = null,
  motionCapability = null,
  onSave,
  eventModeAvailable = true,
  motionModeAvailable = true,
}: RecordingConfigPanelProps) {
  const [form, setForm] = useState<RecordingConfig | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({ ...config });
      setDirty(false);
    }
  }, [config]);

  const modes = options.modes || Object.keys(MODE_LABELS);
  const eventTypes = options.event_types || Object.keys(EVENT_TYPE_LABELS);
  const motionSources = options.motion_sources || Object.keys(MOTION_SOURCE_LABELS);

  const slotSeconds = defaults.segment_seconds || 300;
  const maxPreRoll = defaults.max_pre_roll_seconds ?? 600;

  const set = <K extends keyof RecordingConfig>(key: K, value: RecordingConfig[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  const toggleEventType = (type: string) => {
    if (!form) return;
    const next = form.event_types.includes(type)
      ? form.event_types.filter((t) => t !== type)
      : [...form.event_types, type];
    set('event_types', next);
  };

  /**
   * Per-mode storage estimate. This is the whole business case for the feature, so show it
   * at the point of decision rather than making the operator guess.
   *
   * Triggered modes are estimated in whole 5-minute SLOTS, not pre+post seconds, because
   * that is what actually gets retained (see the plan's slot-granular note). Anything else
   * would under-promise the disk use by ~10x.
   */
  const estimates = useMemo(() => {
    if (!bitrateKbps || bitrateKbps <= 0) return null;
    const eventsPerHour = 20; // illustrative rate, stated in the caption
    const slotsPerHour = Math.min(3600 / slotSeconds, eventsPerHour);
    const triggeredDuty = (slotsPerHour * slotSeconds) / 3600;
    return {
      continuous: GB_PER_DAY(bitrateKbps, 1),
      triggered: GB_PER_DAY(bitrateKbps, triggeredDuty),
      eventsPerHour,
      dutyPct: Math.round(triggeredDuty * 100),
    };
  }, [bitrateKbps, slotSeconds]);

  if (!form) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 shadow-card">
        <h3 className={sectionCls}>Recording</h3>
        <p className="mt-2 text-[12px] text-muted-foreground">
          {loading ? 'Loading recording configuration…' : 'No recording configuration available.'}
        </p>
      </div>
    );
  }

  const triggered = form.mode !== 'continuous';
  const usesEvents = form.mode === 'event' || form.mode === 'event_motion';
  const usesMotion = form.mode === 'motion' || form.mode === 'event_motion';
  const disabled = !canEdit || loading;

  const modeDisabled = (mode: string) => {
    if (mode === 'continuous') return false;
    if (mode === 'event') return !eventModeAvailable;
    if (mode === 'motion') return !motionModeAvailable;
    return !eventModeAvailable || !motionModeAvailable;
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-card space-y-6">
      {/* Header + master toggle */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className={sectionCls}>Recording</h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Choose what gets kept. Live video and analytics are unaffected by this setting.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11px] font-medium text-muted-foreground">
            {form.enabled ? 'Enabled' : 'Disabled'}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={form.enabled}
            aria-label="Enable recording"
            disabled={disabled}
            onClick={() => set('enabled', !form.enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-40 ${
              form.enabled ? 'bg-brand' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                form.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {!form.enabled && (
        <div className="rounded-lg border border-border bg-background p-3 text-[12px] text-muted-foreground">
          Recording is off for this camera — no pipeline runs and nothing is written to disk.
          Live view and analytics continue to work.
        </div>
      )}

      {form.enabled && (
        <>
          {/* ── Mode ── */}
          <div className="space-y-2">
            <p className={sectionCls}>Mode</p>
            <div className="space-y-2">
              {modes.map((mode) => {
                const meta = MODE_LABELS[mode] || { label: mode, hint: '' };
                const isOn = form.mode === mode;
                const unavailable = modeDisabled(mode);
                return (
                  <label
                    key={mode}
                    // The selected tint is an inline color-mix, NOT `bg-brand/5`: Tailwind v3
                    // silently drops opacity modifiers on colours defined as bare `var()`
                    // (as `brand` is here), so the utility would compile to nothing and the
                    // selected row would look identical to the others. Same reason StatusTag
                    // mixes its tints inline.
                    style={isOn ? { backgroundColor: 'color-mix(in srgb, var(--brand) 8%, transparent)' } : undefined}
                    className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${
                      isOn ? 'border-brand' : 'border-border bg-background'
                    } ${unavailable || disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-muted'}`}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="recording-mode"
                        checked={isOn}
                        disabled={disabled || unavailable}
                        onChange={() => set('mode', mode as RecordingConfig['mode'])}
                        className="text-brand focus:ring-brand-ring"
                      />
                      <span>
                        <span className="block text-[13px] font-medium text-foreground">
                          {meta.label}
                          {unavailable && (
                            <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                              (available in a later release)
                            </span>
                          )}
                        </span>
                        <span className="block text-[11px] text-muted-foreground">{meta.hint}</span>
                      </span>
                    </span>
                    {estimates && (
                      <span className="shrink-0 text-right">
                        <span className="block text-[12px] font-medium text-foreground">
                          ~{(mode === 'continuous' ? estimates.continuous : estimates.triggered).toFixed(1)} GB/day
                        </span>
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            {estimates && (
              <p className="text-[11px] text-muted-foreground">
                Estimates use this camera&apos;s measured bitrate. Triggered modes assume{' '}
                {estimates.eventsPerHour} events/hour (~{estimates.dutyPct}% of the day retained).
              </p>
            )}
          </div>

          {/* ── Event triggers ── */}
          {usesEvents && (
            <div className="space-y-3 border-t border-border pt-5">
              <p className={sectionCls}>Event triggers</p>
              <p className="text-[11px] text-muted-foreground">{FIELD_HINTS.event_types}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {eventTypes.map((type) => {
                  const timerDriven = TIMER_DRIVEN_EVENT_TYPES.has(type);
                  return (
                    <label
                      key={type}
                      title={timerDriven
                        ? 'Reports on a timer (about every 60s), so this keeps footage continuously'
                        : undefined}
                      className={`flex items-center gap-2 text-[12px] text-foreground ${
                        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={form.event_types.includes(type)}
                        disabled={disabled}
                        onChange={() => toggleEventType(type)}
                        className="rounded border-border text-brand focus:ring-brand-ring"
                      />
                      <span>
                        {EVENT_TYPE_LABELS[type] || type}
                        {timerDriven && <span className="text-muted-foreground"> ⏱</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
              {/* Selecting a timer-driven category is a legitimate choice, but it means
                  "keep everything" — say so before the operator discovers it from the disk. */}
              {form.event_types.some((t) => TIMER_DRIVEN_EVENT_TYPES.has(t)) && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  ⏱ People counting reports about every 60 seconds, so footage will be kept
                  almost continuously. Use <span className="font-medium">Person</span> instead to
                  keep footage only when someone is actually detected.
                </p>
              )}
              <div>
                <label className={labelCls}>
                  Minimum confidence — {form.min_confidence.toFixed(2)}
                </label>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={form.min_confidence}
                  disabled={disabled}
                  onChange={(e) => set('min_confidence', Number(e.target.value))}
                  className="w-full accent-brand disabled:opacity-40"
                />
                <p className={hintCls}>{FIELD_HINTS.min_confidence}</p>
              </div>
            </div>
          )}

          {/* ── Motion ── */}
          {usesMotion && (
            <div className="space-y-3 border-t border-border pt-5">
              <div className="flex items-center justify-between">
                <p className={sectionCls}>Motion</p>
                {motion?.state && motion.state !== 'unavailable' && (
                  <StatusTag
                    tone={
                      motion.state === 'active'
                        ? 'warning'
                        : motion.state === 'idle'
                          ? 'success'
                          : 'neutral'
                    }
                  >
                    {motion.state}
                  </StatusTag>
                )}
              </div>
              <div>
                <label className={labelCls}>Source</label>
                <select
                  value={form.motion_source}
                  disabled={disabled}
                  onChange={(e) => set('motion_source', e.target.value as RecordingConfig['motion_source'])}
                  className={inputCls}
                >
                  {motionSources.map((s) => (
                    <option key={s} value={s}>
                      {MOTION_SOURCE_LABELS[s] || s}
                    </option>
                  ))}
                </select>
                <p className={hintCls}>{FIELD_HINTS.motion_source}</p>
                {/* What `auto` will actually resolve to, stated before the operator commits. */}
                {motionCapability && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {motionCapability.supported
                      ? '✓ This camera reports ONVIF motion events'
                      : 'Camera-side motion events unavailable — the stream detector will be used'}
                  </p>
                )}
              </div>

              {/* ONVIF subscription health. `confirmed_working` is the honest signal: a camera
                  can advertise the motion topic and never fire it because motion detection is
                  switched off in its own web UI, and no ONVIF query reveals that. */}
              {motion?.onvif && (
                <div className="rounded-lg border border-border bg-background p-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">Camera-side events</span>
                    <StatusTag
                      tone={
                        motion.onvif.error
                          ? 'danger'
                          : motion.onvif.confirmed_working
                            ? 'success'
                            : 'warning'
                      }
                    >
                      {motion.onvif.error
                        ? 'error'
                        : motion.onvif.confirmed_working
                          ? 'confirmed'
                          : 'waiting'}
                    </StatusTag>
                  </div>
                  {!motion.onvif.confirmed_working && !motion.onvif.error && (
                    <p className="text-[11px] text-muted-foreground">
                      Subscribed — waiting for the first motion event. If none arrives, check that
                      motion detection is enabled in the camera&apos;s own settings.
                    </p>
                  )}
                  {motion.onvif.error && (
                    <p className="text-[11px] text-muted-foreground">{motion.onvif.error}</p>
                  )}
                  {motion.onvif.last_motion_at && (
                    <p className="text-[11px] text-muted-foreground">
                      Last event {new Date(motion.onvif.last_motion_at).toLocaleTimeString()}
                    </p>
                  )}
                  {/* Clock skew doesn't affect retention (we stamp with our own receipt time)
                      but it breaks event↔recording correlation everywhere else. */}
                  {motion.onvif.clock_skew_seconds != null &&
                    Math.abs(motion.onvif.clock_skew_seconds) > 60 && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400">
                        Camera clock is {Math.round(motion.onvif.clock_skew_seconds)}s off this
                        server — worth correcting.
                      </p>
                    )}
                </div>
              )}
              <div>
                {/* The number 1–10 means nothing on its own. What it actually sets is the bar in
                    the live meter directly below, so the threshold it produces is shown next to
                    it, in the meter's own units — otherwise the two read as unrelated controls
                    and the only way to tune a camera is guesswork. */}
                <label className={labelCls}>
                  Sensitivity — {form.motion_sensitivity} / 10
                  <span className="ml-2 font-normal text-muted-foreground">
                    · records above <span className="font-mono">{motionThresholdFor(form.motion_sensitivity).toFixed(2)}×</span>
                  </span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={form.motion_sensitivity}
                  disabled={disabled}
                  onChange={(e) => set('motion_sensitivity', Number(e.target.value))}
                  className="w-full accent-brand disabled:opacity-40"
                  aria-describedby="motion-sensitivity-hint"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>1 · only close movement</span>
                  <span>10 · almost any change</span>
                </div>
                <p className="mt-1 text-[11px] font-medium text-foreground">
                  {motionSensitivityMeaning(form.motion_sensitivity)}
                </p>
                <p id="motion-sensitivity-hint" className={hintCls}>{FIELD_HINTS.motion_sensitivity}</p>
                {/* The recorder is still using the SAVED value; say so rather than letting the
                    meter below appear to contradict the slider. */}
                {motion?.threshold != null
                  && Math.abs(motion.threshold - motionThresholdFor(form.motion_sensitivity)) > 0.01 && (
                  <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    Not saved yet — the detector is still using{' '}
                    <span className="font-mono">{motion.threshold.toFixed(2)}×</span>.
                  </p>
                )}
              </div>

              {/* Live meter — the only way to answer "why didn't it record?" in the field. */}
              <div className="rounded-lg border border-border bg-background p-3">
                {motion?.state === 'unavailable' || motion?.score == null ? (
                  <p className="text-[11px] text-muted-foreground">
                    Live motion score unavailable — the detector is not running for this camera yet.
                  </p>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between">
                      <span className="text-[11px] text-muted-foreground">
                        Live motion score
                        {/* Which number is which was not obvious from "0.66 / 1.94" alone. */}
                        {motion.threshold != null && <span className="ml-1">(needs {motion.threshold.toFixed(2)}×)</span>}
                      </span>
                      <span className="font-mono text-[12px] text-foreground">
                        {motion.score.toFixed(2)}
                        {motion.threshold != null && (
                          <span className="text-muted-foreground"> / {motion.threshold.toFixed(2)}</span>
                        )}
                      </span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-brand transition-all"
                        style={{
                          width: `${Math.min(100, (motion.score / Math.max(motion.threshold || 1, 0.001)) * 50)}%`,
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Min duration (ms)</label>
                  <input
                    type="number"
                    min={0}
                    max={60000}
                    step={100}
                    value={form.motion_min_duration_ms}
                    disabled={disabled}
                    onChange={(e) => set('motion_min_duration_ms', Number(e.target.value))}
                    className={inputCls}
                  />
                  <p className={hintCls}>{FIELD_HINTS.motion_min_duration}</p>
                </div>
                <div>
                  <label className={labelCls}>Quiet timeout (ms)</label>
                  <input
                    type="number"
                    min={0}
                    max={600000}
                    step={500}
                    value={form.motion_idle_timeout_ms}
                    disabled={disabled}
                    onChange={(e) => set('motion_idle_timeout_ms', Number(e.target.value))}
                    className={inputCls}
                  />
                  <p className={hintCls}>{FIELD_HINTS.motion_idle_timeout}</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Clip window ── */}
          {triggered && (
            <div className="space-y-3 border-t border-border pt-5">
              <p className={sectionCls}>Clip window</p>
              {/* One column per field with its own explanation, rather than a single
                  paragraph below: pre-roll and post-roll are routinely mistaken for
                  "clip length", and post-roll's extend-on-retrigger behaviour is invisible
                  from the label alone. */}
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>Pre-roll (s)</label>
                  <input
                    type="number"
                    min={0}
                    max={maxPreRoll}
                    value={form.pre_roll_seconds}
                    disabled={disabled}
                    onChange={(e) => set('pre_roll_seconds', Number(e.target.value))}
                    className={inputCls}
                  />
                  <p className={hintCls}>{FIELD_HINTS.pre_roll}</p>
                </div>
                <div>
                  <label className={labelCls}>Post-roll (s)</label>
                  <input
                    type="number"
                    min={0}
                    max={3600}
                    value={form.post_roll_seconds}
                    disabled={disabled}
                    onChange={(e) => set('post_roll_seconds', Number(e.target.value))}
                    className={inputCls}
                  />
                  <p className={hintCls}>{FIELD_HINTS.post_roll}</p>
                </div>
                <div>
                  <label className={labelCls}>Max clip (s)</label>
                  <input
                    type="number"
                    min={30}
                    max={86400}
                    value={form.max_clip_seconds}
                    disabled={disabled}
                    onChange={(e) => set('max_clip_seconds', Number(e.target.value))}
                    className={inputCls}
                  />
                  <p className={hintCls}>{FIELD_HINTS.max_clip}</p>
                </div>
              </div>
              {/* Retention is slot-granular — say so, or every ticket is "I asked for 30s
                  and got 5 minutes". */}
              <p className="text-[11px] text-muted-foreground">
                Footage is retained in {Math.round(slotSeconds / 60)}-minute blocks, so a clip
                covers at least the block containing the trigger. Pre-roll can be at most{' '}
                {maxPreRoll}s — older footage has already left the buffer.
              </p>
            </div>
          )}

          {/* ── Retention ── */}
          <div className="space-y-3 border-t border-border pt-5">
            <p className={sectionCls}>Retention</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Keep for (days)</label>
                <input
                  type="number"
                  min={0}
                  placeholder={`System default (${
                    triggered
                      ? defaults.retention_days_triggered ?? defaults.retention_days ?? 30
                      : defaults.retention_days ?? 30
                  })`}
                  value={form.retention_days ?? ''}
                  disabled={disabled}
                  onChange={(e) =>
                    set('retention_days', e.target.value === '' ? null : Number(e.target.value))
                  }
                  className={inputCls}
                />
                <p className={hintCls}>{FIELD_HINTS.retention_days}</p>
              </div>
            </div>
            {triggered && (
              <p className="text-[11px] text-muted-foreground">
                Triggered clips are small, so they can be kept much longer than continuous
                footage.
              </p>
            )}
          </div>

          {defaults.retention_enforce === 'log' && triggered && (
            <div className="rounded-lg border border-border bg-background p-3 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">Dry-run mode.</span> All footage is
              still being kept, exactly as in continuous mode — the recorder only records which
              blocks this mode <em>would</em> have dropped, so you can see the saving before
              committing to it. No storage is saved until{' '}
              <span className="font-mono">RECORDING_RETENTION_ENFORCE=enforce</span> is set.
            </div>
          )}
        </>
      )}

      {/* ── Actions ── */}
      <div className="flex items-center gap-3 border-t border-border pt-5">
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={disabled || !dirty}
          className="flex-1 rounded-lg bg-brand py-2.5 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-brand-dark disabled:opacity-50"
        >
          {loading ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
        </button>
      </div>
    </div>
  );
}
